/**
 * trace_id generation and propagation utilities.
 *
 * The trace_id links PostHog events with Sentry errors,
 * enabling "find the error for this user action" workflows.
 *
 * See Part 22 §122 of the Tech Guidelines.
 */

import { TRACE_ID_PREFIX, TRACE_ID_HEADER } from '../constants';

/**
 * Generates a unique trace_id for a user action.
 * Call once per meaningful user action (not per click).
 */
export function generateTraceId(): string {
  // crypto.randomUUID() available in modern browsers and Node 19+
  const uuid =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : fallbackUUID();

  return `${TRACE_ID_PREFIX}${uuid}`;
}

/**
 * Validates that a string is a well-formed trace_id.
 */
export function isValidTraceId(id: string): boolean {
  return (
    typeof id === 'string' && id.startsWith(TRACE_ID_PREFIX) && id.length > TRACE_ID_PREFIX.length
  );
}

/**
 * Creates an interceptor for @tonnie/sdk that attaches trace_id
 * to every outgoing API request via the x-trace-id header.
 *
 * @param getTraceId - Optional getter for a current action trace_id.
 *   When not provided (or returns undefined), a new trace_id is generated per request.
 */
export function createTraceIdInterceptor(
  getTraceId?: () => string | undefined
): (config: RequestInit) => RequestInit {
  return (config: RequestInit): RequestInit => {
    const traceId = getTraceId?.() ?? generateTraceId();
    const headers = new Headers(config.headers);
    headers.set(TRACE_ID_HEADER, traceId);

    return {
      ...config,
      headers,
    };
  };
}

// ── Fallback UUID for environments without crypto.randomUUID ─────────

function fallbackUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
